// src/App.js
import React, { useEffect, useState, useContext } from 'react';
import { Wifi, WifiOff, Loader2, CheckCircle2, Users } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Link, useLocation, Navigate } from 'react-router-dom';
import { syncPendingLogs, syncAppData } from './utils/offlineSync';
import { OfflineModeContext } from './contexts/OfflineModeContext';
import { SocialContext, SocialProvider } from './contexts/SocialContext';
import api, { fetchAppConfig, getUnreadCommentsCount } from './api';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import SelectProfilePage from './pages/SelectProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RoutineListPage from './pages/RoutineListPage';
import ProtectedRoute from './components/ProtectedRoute';
import { parseAndStoreOnboardingCode } from './utils/onboardingUtils';
import OfflineBanner from './components/OfflineBanner';

const SessionPlayerPage = React.lazy(() => import('./pages/SessionPlayerPage'));
const SessionSummaryPage = React.lazy(() => import('./pages/SessionSummaryPage'));
const SessionHistoryPage = React.lazy(() => import('./pages/SessionHistoryPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const SocialDashboard = React.lazy(() => import('./pages/SocialDashboard'));
const RunningDashboard = React.lazy(() => import('./components/RunningDashboard'));
const PublicBookingPage = React.lazy(() => import('./pages/PublicBookingPage'));
const CourtBookingPage = React.lazy(() => import('./pages/CourtBookingPage'));

const RouteLoadingFallback = () => (
  <div className="flex justify-center items-center min-h-[50vh]">
    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
  </div>
);

export const UnreadCommentsContext = React.createContext({ count: 0, setCount: () => {} });

const PUBLIC_PATHS = ['/', '/login', '/register', '/select-profile', '/forgot-password', '/reset-password'];

// Re-export for backward compatibility
export { OfflineModeContext };

const isPublicOrBookingPage = (pathname) => {
  const isBookingDomain = typeof window !== 'undefined' && window.location.hostname.includes('booking');
  if (isBookingDomain) return true;
  if (pathname.startsWith('/session/') || pathname.startsWith('/public-booking') || pathname.startsWith('/reset-password') || PUBLIC_PATHS.includes(pathname)) {
    return true;
  }
  const protectedPrefixes = ['/dashboard', '/session', '/summary', '/history', '/settings', '/court-bookings', '/running-dashboard', '/social'];
  return !protectedPrefixes.some(prefix => pathname.startsWith(prefix));
};

const AppHeader = () => {
  const location = useLocation();
  const { isOffline, syncStatus } = useContext(OfflineModeContext);
  const { count: unreadCount } = useContext(UnreadCommentsContext);
  
  if (isPublicOrBookingPage(location.pathname)) {
    return null;
  }

  return (
    <>
      {isOffline && <OfflineBanner />}
      <header className="bg-gray-900/60 backdrop-blur-md border-b border-gray-700/50 sticky top-0 z-50 p-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="text-xl font-bold text-primary">SoloSync</Link>
          
          {/* Sync Status Indicator */}
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-800/50 border border-gray-700/50 transition-all duration-500">
            {syncStatus === 'syncing' && (
              <div className="flex items-center gap-2 text-teal-400 text-xs font-medium animate-pulse">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="hidden sm:inline">Syncing...</span>
              </div>
            )}
            {syncStatus === 'synced' && (
              <div className="flex items-center gap-2 text-emerald-400 text-xs font-medium animate-fade-out">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Synced</span>
              </div>
            )}
            {syncStatus === 'offline' && (
              <div className="flex items-center gap-2 text-orange-400 text-xs font-medium">
                <WifiOff className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}
            {syncStatus === 'online' && !isOffline && (
              <div className="flex items-center gap-2 text-teal-400 text-xs font-medium opacity-50">
                <Wifi className="w-3.5 h-3.5" />
              </div>
            )}
          </div>
        </div>
        <nav className="hidden md:block">
          <ul className="flex items-center space-x-6">
            <li className="relative">
              <Link to="/history" className="text-gray-200 hover:text-white transition-colors font-medium flex items-center gap-1.5">
                History
                {unreadCount > 0 && (
                  <span className="w-4 h-4 bg-blue-500 text-white text-[9px] font-extrabold rounded-full flex items-center justify-center animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            </li>
            <SocialNavLink />
            <li>
              <Link to="/settings" className="text-gray-200 hover:text-white transition-colors font-medium">Settings</Link>
            </li>
            <li>
              <LogoutButton />
            </li>
          </ul>
        </nav>
      </header>
    </>
  );
};

/**
 * SocialNavLink — desktop header Social link, hidden when kill switch is off.
 * Rendered as a <li> so it slots directly into the existing <ul> in AppHeader.
 */
const SocialNavLink = () => {
  const { socialFeaturesEnabled, pendingRequests } = useContext(SocialContext);
  if (!socialFeaturesEnabled) return null;
  return (
    <li className="relative">
      <Link to="/social" id="desktop-nav-social" className="text-gray-200 hover:text-white transition-colors font-medium flex items-center gap-1.5">
        Social
        {pendingRequests > 0 && (
          <span className="w-4 h-4 bg-amber-400 text-gray-900 text-[9px] font-extrabold rounded-full flex items-center justify-center">
            {pendingRequests > 9 ? '9+' : pendingRequests}
          </span>
        )}
      </Link>
    </li>
  );
};

const MobileBottomNav = () => {
  const location = useLocation();
  const { socialFeaturesEnabled, pendingRequests } = useContext(SocialContext);
  const { count: unreadCount } = useContext(UnreadCommentsContext);
  
  if (isPublicOrBookingPage(location.pathname)) {
    return null;
  }

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900/80 backdrop-blur-md border-t border-gray-800 z-50 pb-safe" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <ul className="flex justify-around items-center h-16">
        <li>
          <Link to="/dashboard" className={`flex flex-col items-center p-2 ${location.pathname === '/dashboard' ? 'text-primary' : 'text-gray-400'}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
            <span className="text-[10px] mt-1">Home</span>
          </Link>
        </li>
        <li>
          <Link to="/history" className={`flex flex-col items-center p-2 ${location.pathname === '/history' ? 'text-primary' : 'text-gray-400'}`}>
            <div className="relative">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-[9px] font-extrabold rounded-full flex items-center justify-center animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-1">History</span>
          </Link>
        </li>
        {socialFeaturesEnabled && (
          <li className="relative">
            <Link to="/social" id="nav-social-tab" className={`flex flex-col items-center p-2 ${location.pathname === '/social' ? 'text-primary' : 'text-gray-400'}`}>
              <div className="relative">
                <Users className="w-6 h-6" />
                {pendingRequests > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 text-gray-900 text-[9px] font-extrabold rounded-full flex items-center justify-center">
                    {pendingRequests > 9 ? '9+' : pendingRequests}
                  </span>
                )}
              </div>
              <span className="text-[10px] mt-1">Social</span>
            </Link>
          </li>
        )}
        <li>
          <Link to="/settings" className={`flex flex-col items-center p-2 ${location.pathname === '/settings' ? 'text-primary' : 'text-gray-400'}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            <span className="text-[10px] mt-1">Settings</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

/**
 * SocialProtectedRoute — guards /social against both unauthenticated users
 * and cases where the kill switch is disabled.
 */
const SocialProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('access_token');
  const { socialFeaturesEnabled } = useContext(SocialContext);
  if (!token) return <Navigate to="/login" replace />;
  if (!socialFeaturesEnabled) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncStatus, setSyncStatus] = useState(navigator.onLine ? 'online' : 'offline');
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  useEffect(() => {
    const handleNewContentAvailable = (event) => {
      const registration = event.detail;
      if (registration && registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdatePrompt(true);
      }
    };

    window.addEventListener('newContentAvailable', handleNewContentAvailable);

    // Check for an already waiting worker and listen to controllerchange
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && reg.waiting) {
          setWaitingWorker(reg.waiting);
          setShowUpdatePrompt(true);
        }
      });

      let refreshing = false;
      const handleControllerChange = () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      return () => {
        window.removeEventListener('newContentAvailable', handleNewContentAvailable);
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }

    return () => window.removeEventListener('newContentAvailable', handleNewContentAvailable);
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // If the worker has already activated, reload immediately
      if (waitingWorker.state === 'activated') {
        window.location.reload();
        return;
      }
      waitingWorker.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') {
          window.location.reload();
        }
      });
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      // Fallback: If neither controllerchange nor statechange fires within 500ms, reload anyway
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      window.location.reload();
    }
  };

  return (
    <OfflineModeContext.Provider value={{ isOffline, syncStatus }}>
      <SocialProvider>
        {showUpdatePrompt && (
          <div className="fixed top-0 left-0 right-0 z-[100] bg-emerald-600 text-white px-4 py-3 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 shadow-lg">
            <span className="font-medium text-sm sm:text-base text-center">A new version of SoloSync2 is available!</span>
            <button
              onClick={handleUpdate}
              className="bg-white text-emerald-600 px-5 py-1.5 rounded-full text-sm font-bold shadow-md hover:bg-emerald-50 transition-colors whitespace-nowrap"
            >
              Update Now
            </button>
          </div>
        )}
        <AppInner
          isOffline={isOffline}
          setIsOffline={setIsOffline}
          setSyncStatus={setSyncStatus}
        />
      </SocialProvider>
    </OfflineModeContext.Provider>
  );
}

const OnboardingCodeHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (code) {
      parseAndStoreOnboardingCode(location.search);
      const isLoggedIn = !!localStorage.getItem('access_token');
      if (isLoggedIn) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/register', { replace: true });
      }
    }
  }, [location.search, navigate]);

  return null;
};

/**
 * AppInner is separated so that SocialContext is available to all hooks
 * that need both OfflineModeContext and SocialContext.
 */
function AppInner({ isOffline, setIsOffline, setSyncStatus }) {
  const { setSocialFeaturesEnabled, disableSocialFeatures } = useContext(SocialContext);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const performSync = async () => {
      if (!navigator.onLine) {
        setSyncStatus('offline');
        return;
      }
      setSyncStatus('syncing');
      try {
        await Promise.all([
          syncPendingLogs(api),
          syncAppData(api)
        ]);
        setSyncStatus('synced');
        setTimeout(() => setSyncStatus('online'), 3000);
      } catch (err) {
        console.error('Initial sync failed:', err);
        setSyncStatus('online');
      }
    };

    // Fetch app config (no auth required) to initialise the social kill switch
    const loadConfig = async () => {
      const config = await fetchAppConfig();
      if (config?.social_features_enabled === true) {
        setSocialFeaturesEnabled(true);
      }
    };

    const fetchUnreadCount = async () => {
      try {
        const response = await getUnreadCommentsCount();
        if (response && response.data) {
          setUnreadCount(response.data.count);
        }
      } catch (err) {
        console.error('Failed to fetch unread comments count:', err);
      }
    };

    loadConfig();

    if (localStorage.getItem('access_token')) {
      performSync();
      fetchUnreadCount();
    }

    const handleOnline = () => { setIsOffline(false); performSync(); };
    const handleOffline = () => { setIsOffline(true); setSyncStatus('offline'); };
    const handleOfflineMode = () => setIsOffline(true);
    const handleOnlineMode = () => setIsOffline(false);
    // Kill-switch event dispatched by the Axios interceptor on social 503s
    const handleSocialDisabled = () => disableSocialFeatures();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('offlineModeTriggered', handleOfflineMode);
    window.addEventListener('onlineModeTriggered', handleOnlineMode);
    window.addEventListener('socialFeaturesDisabled', handleSocialDisabled);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offlineModeTriggered', handleOfflineMode);
      window.removeEventListener('onlineModeTriggered', handleOnlineMode);
      window.removeEventListener('socialFeaturesDisabled', handleSocialDisabled);
    };
  }, [setIsOffline, setSyncStatus, setSocialFeaturesEnabled, disableSocialFeatures]);

  return (
    <UnreadCommentsContext.Provider value={{ count: unreadCount, setCount: setUnreadCount }}>
      <Router>
        <OnboardingCodeHandler />
        <div className="App flex flex-col min-h-[100dvh]">
          <AppHeader />
          <main className="flex-grow pb-24 md:pb-8">
          <React.Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
            <Route path="/select-profile" element={<SelectProfilePage />} />
            <Route path="/public-booking/:venueSlug" element={<PublicBookingPage />} />
            <Route path="/public-booking" element={<PublicBookingPage />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={<ProtectedRoute><RoutineListPage /></ProtectedRoute>} />
            <Route path="/session/:id" element={<ProtectedRoute><SessionPlayerPage /></ProtectedRoute>} />
            <Route path="/summary" element={<ProtectedRoute><SessionSummaryPage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><SessionHistoryPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/court-bookings" element={<ProtectedRoute><CourtBookingPage /></ProtectedRoute>} />
            <Route path="/running-dashboard" element={<ProtectedRoute><RunningDashboard /></ProtectedRoute>} />

            {/* Social — kill-switch protected */}
            <Route path="/social" element={<SocialProtectedRoute><SocialDashboard /></SocialProtectedRoute>} />

            {/* Direct public venue slug matching for booking.squashsync.com/<venue-slug> */}
            <Route path="/:venueSlug" element={<PublicBookingPage />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </React.Suspense>
        </main>
        <MobileBottomNav />
      </div>
    </Router>
    </UnreadCommentsContext.Provider>
  );
}

const LogoutButton = () => {
  const navigate = useNavigate();
  const { isOffline } = useContext(OfflineModeContext);

  const handleLogout = () => {
    if (isOffline) {
      alert("Logging out is disabled while in offline mode.");
      return;
    }
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      navigate('/login');
    }
  };

  // Only show the logout button if the user is logged in
  if (!localStorage.getItem('access_token')) {
    return null;
  }

  return (
    <button
      onClick={handleLogout}
      disabled={isOffline}
      className={`font-bold py-2 px-4 rounded transition-colors ${
        isOffline 
          ? 'bg-gray-500/10 text-gray-500 border border-gray-500/20 cursor-not-allowed' 
          : 'bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20'
      }`}
    >
      Logout
    </button>
  );
};

export default App;